import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatErr } from "@/lib/api";
import {
  CheckCircle2, Loader2, CalendarClock, MapPin, Video, BadgeCheck, Mail, ExternalLink, ClipboardCopy,
} from "lucide-react";
import { toast } from "sonner";

/**
 * Guest booking access page.
 *
 * Anyone holding the `guest_token` UUID can view their booking, receipt and
 * (for online calls) join the meeting URL. No account required.
 * Route: `/booking/access/:token`.
 */
export default function GuestBookingAccess() {
  const { token } = useParams();
  const [b, setB] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    const tick = async () => {
      try {
        const { data } = await api.get(`/bookings/guest/${token}`);
        if (!alive) return;
        setB(data);
        if (data.payment_status !== "paid") setTimeout(tick, 2500);
      } catch (e) {
        if (alive) setErr(formatErr(e));
      } finally {
        if (alive) setLoading(false);
      }
    };
    tick();
    return () => { alive = false; };
  }, [token]);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      toast.success("Link copied");
    } catch { /* clipboard blocked */ }
  };

  if (loading) {
    return (
      <div className="max-w-xl mx-auto p-20 text-center" data-testid="guest-booking-loading">
        <Loader2 size={32} className="mx-auto animate-spin"/>
        <p className="mt-4 text-[#8A8A8A]">Loading your booking…</p>
      </div>
    );
  }
  if (err || !b) {
    return (
      <div className="max-w-xl mx-auto p-16 text-center" data-testid="guest-booking-error">
        <h1 className="h-display text-3xl text-[#B84B4B]">Booking not found</h1>
        <p className="text-[#4A4A4A] mt-2">{err || "Check the link in your email and try again."}</p>
        <Link to="/" className="btn-ghost mt-6 inline-flex">Back to Hoomanz</Link>
      </div>
    );
  }

  const paid = b.payment_status === "paid";
  const isOnline = b.delivery_type === "online";

  return (
    <div className="max-w-2xl mx-auto px-6 py-12" data-testid="guest-booking-page">
      <div className="text-center">
        {paid
          ? <CheckCircle2 size={44} className="mx-auto text-[#0A0A0A]"/>
          : <Loader2 size={32} className="mx-auto animate-spin text-[#8A8A8A]"/>}
        <p className="overline mt-4">{paid ? "Booking confirmed" : "Waiting for payment"}</p>
        <h1 className="h-display text-3xl mt-1" data-testid="guest-booking-title">{b.service_title}</h1>
        <p className="text-[#4A4A4A] mt-2">
          {b.currency?.toUpperCase()} ${Number(b.price).toFixed(2)}
          {b.customer_email && <> · sent to <b>{b.customer_email}</b></>}
        </p>
      </div>

      {/* Hooman card */}
      {b.hooman && (
        <div className="card p-5 mt-8 flex items-center gap-4" data-testid="guest-booking-hooman">
          {b.hooman.avatar && <img src={b.hooman.avatar} alt="" className="h-14 w-14 rounded-full object-cover bg-[#E5E5E3]"/>}
          <div className="flex-1 min-w-0">
            <p className="text-[11px] text-[#8A8A8A] uppercase tracking-wide">Your Hooman</p>
            <p className="font-medium inline-flex items-center gap-1.5">
              {b.hooman.name}
              {b.hooman.verified && <BadgeCheck size={13} className="text-[#0057FF]"/>}
            </p>
            <p className="text-xs text-[#8A8A8A]">{b.hooman.category}{b.hooman.location && ` · ${b.hooman.location}`}</p>
          </div>
          <Link to={`/u/${b.hooman.id}`} className="btn-ghost text-xs" data-testid="guest-booking-view-hooman">View profile</Link>
        </div>
      )}

      {/* Booking details */}
      <div className="card p-6 mt-4" data-testid="guest-booking-details">
        <p className="text-xs uppercase tracking-wider text-[#8A8A8A] mb-3">Details</p>
        <Row icon={<CalendarClock size={13}/>} label="When" value={b.date}/>
        <Row icon={isOnline ? <Video size={13}/> : <MapPin size={13}/>} label="How"
             value={isOnline ? "Online video call (Jitsi Meet)" : "In person"}/>
        {b.notes && (
          <div className="mt-3">
            <p className="text-xs uppercase tracking-wider text-[#8A8A8A]">Your note</p>
            <p className="text-sm text-[#0A0A0A] mt-1 whitespace-pre-wrap">{b.notes}</p>
          </div>
        )}

        {paid && isOnline && (
          b.meeting_url ? (
            <a href={b.meeting_url} target="_blank" rel="noreferrer"
               className="btn-primary w-full mt-6"
               data-testid="guest-booking-join-call">
              <Video size={16}/> Join video call <ExternalLink size={13}/>
            </a>
          ) : (
            <p className="text-sm text-[#8A8A8A] mt-6 text-center">
              Your meeting link will appear here once payment clears.
            </p>
          )
        )}
        {paid && !isOnline && (
          <p className="text-sm text-[#4A4A4A] mt-6 text-center">
            Your Hooman will message you at <b>{b.customer_email}</b> with the meeting spot.
          </p>
        )}
        {!paid && (
          <div className="mt-6 text-center">
            <Link to={`/checkout/${b.id}?t=${token}`} className="btn-primary" data-testid="guest-booking-pay-now">
              Complete payment
            </Link>
            <p className="text-xs text-[#8A8A8A] mt-2">Card / Apple Pay / Google Pay via Stripe.</p>
          </div>
        )}
      </div>

      {/* Summary */}
      <div className="card p-5 mt-4" data-testid="guest-booking-summary">
        <p className="text-xs uppercase tracking-wider text-[#8A8A8A] mb-2">Summary</p>
        <Row2 label="Service" value={`$${Number(b.price).toFixed(2)}`}/>
        <div className="border-t border-[#E5E5E3] mt-2 pt-2 flex items-baseline justify-between">
          <span className="font-medium">Total {paid ? "paid" : "due"}</span>
          <span className="font-heading text-2xl" data-testid="guest-booking-total">${Number(b.price).toFixed(2)}</span>
        </div>
      </div>

      <div className="mt-8 rounded-2xl bg-[#F8F8F6] p-4 text-sm text-[#4A4A4A]">
        <p className="font-medium text-[#0A0A0A] mb-1"><Mail size={13} className="inline mr-1"/> Keep this link.</p>
        <p>
          Anyone with this URL can open your booking + join your call. Bookmark it, or find it in the email we sent to <b>{b.customer_email || "your inbox"}</b>.
          Want everything in one place next time? <Link to="/register" className="underline text-[#0057FF]">Create a free Hoomanz account</Link>.
        </p>
        <button onClick={copyLink}
                className="mt-3 btn-ghost text-xs inline-flex items-center gap-1.5"
                data-testid="guest-booking-copy-link">
          <ClipboardCopy size={12}/> Copy this link
        </button>
      </div>
    </div>
  );
}

function Row({ icon, label, value }) {
  return (
    <div className="flex items-start gap-2 py-1.5">
      <span className="text-[#0057FF] mt-0.5">{icon}</span>
      <div>
        <p className="text-[11px] uppercase tracking-wider text-[#8A8A8A]">{label}</p>
        <p className="text-sm font-medium">{value}</p>
      </div>
    </div>
  );
}
function Row2({ label, value }) {
  return (
    <div className="flex items-center justify-between text-sm">
      <span className="text-[#4A4A4A]">{label}</span>
      <span>{value}</span>
    </div>
  );
}
